import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Plus, Building, CreditCard, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../../services/api';
import AddAccountModal from '../modals/AddAccountModal';
import AccountDetailsModal from '../modals/AccountDetailsModal';
import EditAccountModal from '../modals/EditAccountModal';
import PageHeader from '../common/PageHeader';

const AccountsTab = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAccounts();
      setAccounts(response);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleAddAccount = async (accountData) => {
    try {
      const newAccount = await apiService.createAccount(accountData);
      setAccounts(prevAccounts => [...prevAccounts, newAccount]);
    } catch (err) {
      console.error('Error creating account:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const handleViewDetails = (account) => {
    setSelectedAccount(account);
    setIsDetailsModalOpen(true);
  };

  const handleAccountClick = (account) => {
    navigate(`/accounts/${account.id}`);
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setIsEditModalOpen(true);
  };

  const handleUpdateAccount = async (accountId, accountData) => {
    try {
      const updatedAccount = await apiService.updateAccount(accountId, accountData);
      setAccounts(prevAccounts =>
        prevAccounts.map(account =>
          account.id === accountId ? updatedAccount : account
        )
      );
    } catch (err) {
      console.error('Error updating account:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await apiService.deleteAccount(accountId);
      // Remove account from the list (for both soft and hard delete)
      setAccounts(prevAccounts =>
        prevAccounts.filter(account => account.id !== accountId)
      );
    } catch (err) {
      console.error('Error deleting account:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatLastImport = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
      }
      return diffHours === 1 ? '1h ago' : `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getLastImportColor = (dateString) => {
    if (!dateString) return 'text-gray-400 dark:text-gray-500';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 3) {
      return 'text-green-600 dark:text-green-400';
    } else if (diffDays <= 7) {
      return 'text-yellow-600 dark:text-yellow-400';
    } else {
      return 'text-red-600 dark:text-red-400';
    }
  };

  const toggleGroupCollapse = (accountType) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(accountType)) {
      newCollapsed.delete(accountType);
    } else {
      newCollapsed.add(accountType);
    }
    setCollapsedGroups(newCollapsed);
  };

  const getAccountIcon = (accountType) => {
    switch (accountType) {
      case 'brokerage':
        return Building;
      case 'bank':
        return Wallet;
      case 'cash':
        return Wallet;
      case 'retirement':
        return CreditCard;
      case 'savings':
        return Wallet;
      case 'checking':
        return Wallet;
      case 'investment':
        return Building;
      case 'credit_card':
        return CreditCard;
      case 'loan':
        return CreditCard;
      default:
        return Wallet;
    }
  };

  const getAccountTypeDisplayName = (type) => {
    switch (type) {
      case 'brokerage':
        return 'Brokerage Accounts';
      case 'bank':
        return 'Bank Accounts';
      case 'cash':
        return 'Cash';
      case 'retirement':
        return 'Retirement Accounts';
      case 'savings':
        return 'Savings Accounts';
      case 'checking':
        return 'Checking Accounts';
      case 'investment':
        return 'Investment Accounts';
      case 'credit_card':
        return 'Credit Cards';
      case 'loan':
        return 'Loan Accounts';
      default:
        return 'Other Accounts';
    }
  };

  // Helper function to get display balance (market value if available, otherwise cash balance)
  const getAccountDisplayBalance = (account) => {
    // For investment accounts with holdings, show market value + cash
    if (account.market_value && account.market_value > 0) {
      return account.market_value + (account.balance || 0);
    }
    // Otherwise just show cash balance
    return account.balance || 0;
  };

  // Group accounts by type and calculate totals
  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.account_type || 'other';
    if (!groups[type]) {
      groups[type] = {
        accounts: [],
        total: 0
      };
    }
    groups[type].accounts.push(account);
    groups[type].total += getAccountDisplayBalance(account);
    return groups;
  }, {});

  // Calculate overall total
  const overallTotal = accounts.reduce((total, account) => total + getAccountDisplayBalance(account), 0);

  // Define display order for account types
  const accountTypeOrder = [
    'cash',
    'checking',
    'savings',
    'bank',
    'credit_card',
    'investment',
    'brokerage',
    'retirement',
    'loan',
    'other'
  ];

  const renderStats = () => {
    if (loading || error || accounts.length === 0) return null;

    return (
      <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">Total Net Worth</span>
          <span className={`text-xl font-bold ${
            overallTotal < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
          }`}>
            {formatCurrency(overallTotal)}
          </span>
        </div>
        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
          Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        icon={Wallet}
        title="Accounts"
        subtitle="Manage your financial accounts"
        stats={renderStats()}
      >
        <button
          onClick={loadAccounts}
          disabled={loading}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''} sm:mr-2`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
        <button
          type="button"
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
        >
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Account</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-3 sm:px-4 lg:px-8">

      {initialLoading && (
        <div className="text-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-500">Loading accounts...</p>
        </div>
      )}

      {!initialLoading && error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700">{error}</div>
          <button
            onClick={loadAccounts}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {!initialLoading && !error && accounts.length === 0 && (
        <div className="text-center py-12">
          <Wallet className="h-12 w-12 mx-auto text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No accounts found</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding your first account</p>
        </div>
      )}

      {!initialLoading && !error && accounts.length > 0 && (
        <div className="space-y-6 sm:space-y-8">
          {accountTypeOrder
            .filter(type => groupedAccounts[type] && groupedAccounts[type].accounts.length > 0)
            .map((accountType) => {
              const group = groupedAccounts[accountType];
              const TypeIcon = getAccountIcon(accountType);

              const isCollapsed = collapsedGroups.has(accountType);
              const ChevronIcon = isCollapsed ? ChevronDown : ChevronUp;

              return (
                <div key={accountType} className="space-y-3 sm:space-y-4">
                  {/* Account Type Header with Total */}
                  <div
                    className="border-b border-gray-200 dark:border-gray-700 pb-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t-lg px-2 py-1 -mx-2"
                    onClick={() => toggleGroupCollapse(accountType)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0 flex-1">
                        <ChevronIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500 mr-1 sm:mr-2 flex-shrink-0" />
                        <TypeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 dark:text-gray-500 mr-2 sm:mr-3 flex-shrink-0" />
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                          {getAccountTypeDisplayName(accountType)}
                        </h2>
                        <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                          ({group.accounts.length})
                        </span>
                      </div>
                      <div className={`text-base sm:text-lg font-bold whitespace-nowrap ml-2 ${
                        group.total < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                      }`}>
                        {formatCurrency(group.total)}
                      </div>
                    </div>
                  </div>

                  {/* Accounts in this group */}
                  {!isCollapsed && (
                    <div className="space-y-2 sm:space-y-3">
                    {group.accounts.map((account) => {
                      const Icon = getAccountIcon(account.account_type);
                      return (
                        <div
                          key={account.id}
                          className="bg-white dark:bg-gray-800 shadow rounded-lg cursor-pointer hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 ml-0 sm:ml-4"
                          onClick={() => handleAccountClick(account)}
                        >
                          {/* Desktop View */}
                          <div className="hidden sm:block px-4 py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center min-w-0 flex-1">
                                <div className="flex-shrink-0">
                                  <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                </div>
                                <div className="ml-3 min-w-0 flex-1">
                                  <div className="flex items-baseline gap-2">
                                    <h3 className="text-base font-medium text-gray-900 dark:text-white">
                                      {account.name}
                                    </h3>
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                      •••{account.account_number.slice(-4)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span>{account.institution}</span>
                                    {account.last_import && (
                                      <>
                                        <span className="text-gray-300 dark:text-gray-600">•</span>
                                        <span className={`text-xs ${getLastImportColor(account.last_import)}`}>Imported {formatLastImport(account.last_import)}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right ml-4 flex-shrink-0">
                                <div className={`text-lg font-bold ${
                                  getAccountDisplayBalance(account) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                                }`}>
                                  {formatCurrency(getAccountDisplayBalance(account))}
                                </div>
                                <div className="flex justify-end mt-1 space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDetails(account);
                                    }}
                                    className="text-xs text-green-600 dark:text-green-500 hover:text-green-700 dark:hover:text-green-400"
                                  >
                                    Details
                                  </button>
                                  <span className="text-gray-300 dark:text-gray-600">•</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(account);
                                    }}
                                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Mobile View */}
                          <div className="sm:hidden px-3 py-2.5">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-baseline gap-1.5">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {account.name}
                                    </h3>
                                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                      •••{account.account_number.slice(-4)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {account.institution}
                                    </p>
                                    {account.last_import && (
                                      <>
                                        <span className="text-gray-300 dark:text-gray-600">•</span>
                                        <span className={`text-xs ${getLastImportColor(account.last_import)}`}>{formatLastImport(account.last_import)}</span>
                                      </>
                                    )}
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <span className="text-gray-300 dark:text-gray-600">•</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewDetails(account);
                                        }}
                                        className="text-xs text-green-600 dark:text-green-500 hover:text-green-700 dark:hover:text-green-400"
                                      >
                                        Details
                                      </button>
                                      <span className="text-gray-300 dark:text-gray-600">•</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEdit(account);
                                        }}
                                        className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className={`text-base font-bold whitespace-nowrap ml-2 ${
                                getAccountDisplayBalance(account) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'
                              }`}>
                                {formatCurrency(getAccountDisplayBalance(account))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      <AddAccountModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAccountAdded={handleAddAccount}
      />

      <AccountDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        account={selectedAccount}
        onAccountDeleted={handleDeleteAccount}
      />

      <EditAccountModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        account={selectedAccount}
        onAccountUpdated={handleUpdateAccount}
      />
      </div>
    </div>
  );
};

export default AccountsTab;